import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { useUserCompaniesStore } from '../stores/userCompanies.store';

interface UseDocumentsDeepLinkParams {
  nodes: DocumentNodeWithDetailsDto[] | undefined;
  isFetching: boolean;
  isInsideFolder: boolean;
}

export const useDocumentsDeepLink = ({
  nodes,
  isFetching,
  isInsideFolder,
}: UseDocumentsDeepLinkParams): { highlightedNodeId: string | null } => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);
  const companies = useUserCompaniesStore((state) => state.companies);

  const requestedCompanyId = searchParams.get('company');
  const highlightedNodeId = searchParams.get('highlight');

  useEffect(() => {
    if (!requestedCompanyId || companies.length === 0) {
      return;
    }

    const isMember = companies.some((company) => company.id === requestedCompanyId);

    if (isMember && requestedCompanyId !== selectedCompanyId) {
      setSelectedCompany(requestedCompanyId);
      return;
    }

    setSearchParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        nextParams.delete('company');
        if (!isMember) {
          nextParams.delete('highlight');
        }
        return nextParams;
      },
      { replace: true },
    );

    if (!isMember) {
      toast.error(t('documents.deep_link.forbidden_company'));
    }
  }, [requestedCompanyId, companies, selectedCompanyId, setSelectedCompany, setSearchParams, t]);

  useEffect(() => {
    if (!highlightedNodeId || requestedCompanyId || !isInsideFolder) {
      return;
    }

    if (isFetching || !nodes) {
      return;
    }

    if (nodes.some((node) => node.id === highlightedNodeId)) {
      return;
    }

    setSearchParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        nextParams.delete('highlight');
        return nextParams;
      },
      { replace: true },
    );
    toast.info(t('documents.deep_link.node_not_found'));
  }, [
    highlightedNodeId,
    requestedCompanyId,
    isInsideFolder,
    isFetching,
    nodes,
    setSearchParams,
    t,
  ]);

  return { highlightedNodeId };
};
